import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact dashboard widget that surfaces deals with high views but
 * 0 enquiries (or sub-threshold conversion). Hidden entirely when
 * nothing qualifies — so admins only see it when there's actionable signal.
 */
export const ConversionAttentionCard = ({ getStoredPassword, onOpenAnalytics }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const pwd = encodeURIComponent(getStoredPassword?.() || '');
        const r = await fetch(
          `${API_URL}/api/admin/deals/conversion-attention?password=${pwd}&min_views=20&max_conversion_pct=3&days=30&limit=5`
        );
        if (r.ok) setData(await r.json());
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, [getStoredPassword]);

  if (loading || !data || (data.deals || []).length === 0) return null;

  const deals = data.deals || [];

  return (
    <div
      data-testid="conversion-attention-card"
      className="bg-gradient-to-br from-amber-500/5 to-amber-500/0 border border-amber-500/30 rounded-lg p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0 mt-0.5">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-semibold">
              Conversion Attention
            </div>
            <h4 className="text-white font-semibold text-sm mt-0.5">
              {data.total_flagged} deal{data.total_flagged !== 1 ? 's' : ''} pulling traffic but losing enquiries
            </h4>
            <p className="text-white/50 text-[11px] mt-1 leading-relaxed">
              Last {data.window_days} days · {data.min_views}+ views with no enquiries or &lt;{data.max_conversion_pct}% conversion.
              Usually a hero-image, headline or pricing-positioning fix away from doubling enquiries.
            </p>
          </div>
        </div>
        {onOpenAnalytics && (
          <button
            onClick={onOpenAnalytics}
            data-testid="conversion-attention-open-analytics"
            className="text-[10px] uppercase tracking-[0.18em] text-amber-400 hover:text-amber-300 flex items-center gap-1 shrink-0"
          >
            Open analytics <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      <div className="space-y-1.5">
        {deals.map((d) => (
          <div
            key={d.id || d.slug}
            data-testid={`conversion-attention-row-${d.slug}`}
            className="bg-black/30 border border-white/5 rounded-md px-3 py-2 flex items-center gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="text-white text-[12px] truncate">{d.title || d.slug}</div>
              <div className="text-white/40 text-[10px] truncate">
                {[d.location, d.property_type].filter(Boolean).join(' · ') || '—'}
                <span className="ml-2 text-amber-400/80">· {d.reason}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <div className="flex items-center gap-1 text-white/80 text-[11px] tabular-nums">
                  <Eye className="w-3 h-3 text-white/40" />
                  {d.views}
                </div>
                <div className="text-white/40 text-[9px] uppercase tracking-wider">views</div>
              </div>
              <div className="text-right">
                <div className={`text-[11px] tabular-nums font-medium ${d.enquiries === 0 ? 'text-red-400' : 'text-amber-300'}`}>
                  {d.enquiries}
                </div>
                <div className="text-white/40 text-[9px] uppercase tracking-wider">enq</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConversionAttentionCard;
